// Starts a Polar checkout by asking the backend to create a Checkout Session,
// then presenting Polar's hosted checkout. On success Polar redirects back to
// `${returnPath}?payment=success`.
//
// Two presentation modes, chosen at build time via the Vercel env var
// REACT_APP_CHECKOUT_MODE:
//   - 'redirect' (default): full-page redirect to Polar's hosted checkout.
//     Supports Google Pay / Apple Pay wallets, so it's the default.
//   - 'embed': open Polar's checkout as an on-page overlay (keeps the buyer
//     on doyourstep.com). Falls back to a redirect if the embed lib fails.
import { getAttributedRef } from '../utils/referral';
import { trackVirtualPageview } from '../utils/trackVirtualPageview';

// Build-time flag (CRA inlines REACT_APP_* at build). Set to 'embed' in Vercel
// to switch back to the on-page overlay; anything else keeps the redirect.
const CHECKOUT_MODE = (process.env.REACT_APP_CHECKOUT_MODE || 'redirect').toLowerCase();

export interface StartPolarCheckoutOptions {
    email?: string;
    returnPath?: string; // e.g. `/subscription-success/${quizId}`
    metadata?: Record<string, string | number | boolean>;
    /**
     * Promo code granted in the funnel. The backend resolves it to a Polar
     * discount ID; an unresolvable code is dropped there, not here.
     */
    discountCode?: string;
    /** Called once the session exists, just before we leave the page. */
    onSession?: (info: { discountApplied: boolean }) => void;
}

export async function startPolarCheckout(opts: StartPolarCheckoutOptions = {}): Promise<void> {
    // Attach the first-touch referral code (if any) so the webhook can credit
    // the resulting trial/payment to the referrer. See utils/referral.
    const refCode = getAttributedRef();
    const metadata: Record<string, any> = { ...(opts.metadata || {}) };
    if (refCode) metadata.ref_code = refCode;

    const res = await fetch('/api/polar-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            email: opts.email,
            returnPath: opts.returnPath,
            discountCode: opts.discountCode,
            metadata,
        }),
    });

    if (!res.ok) {
        let message = 'checkout_failed';
        try {
            const data = await res.json();
            // Surface the underlying Polar error (data.message) when present so the
            // browser console shows the real cause (e.g. "Unauthorized", "Product not
            // found") instead of only the generic wrapper text.
            const base = data?.error || message;
            message = data?.message ? `${base}: ${data.message}` : base;
        } catch {
            // ignore body parse errors
        }
        throw new Error(message);
    }

    const { url, discountApplied } = await res.json();
    if (!url) {
        throw new Error('checkout_failed: no url returned');
    }

    opts.onSession?.({ discountApplied: !!discountApplied });

    // Funnel: opening the checkout is a distinct step from viewing the paywall.
    // Fire a virtual pageview (…/checkout, replacing the current /paywall segment)
    // so GA/GTM can separate "viewed offer" from "opened checkout".
    const checkoutPath = window.location.pathname.replace(/\/[^/]*$/, '/checkout');
    trackVirtualPageview(checkoutPath);

    // Embedded mode: open Polar's checkout as an overlay on our own page instead
    // of a full-page redirect to polar.sh — keeps the user on-site. On success
    // Polar redirects the parent window to the session's successUrl
    // (`/thank-page?payment=success`), so the post-payment flow is unchanged.
    // Fall back to a plain redirect if the embed script fails to load/open.
    if (CHECKOUT_MODE === 'embed') {
        try {
            const { PolarEmbedCheckout } = await import('@polar-sh/checkout/embed');
            await PolarEmbedCheckout.create(url, { theme: 'light' });
            return;
        } catch {
            // fall through to the redirect below
        }
    }

    // Redirect mode (default): full-page navigation to Polar's hosted checkout.
    // The hosted page offers Google Pay / Apple Pay, which the embed does not.
    window.location.href = url;
}
